import type { ElementType, ReactNode } from 'react';
import {
  Card,
  CardTitle,
  CardContent,
  CardFooter,
} from 'components/ui/card';
import { Button } from 'components/ui/button';
import { Alert, AlertTitle, AlertDescription } from 'components/ui/alert';
import {
  Target,
  RotateCcw,
  Clock,
  Info,
  Play,
  ArrowLeft,
  Loader2,
  CheckCircle2,
} from 'lucide-react';

import type { QuizIntroResponse } from '../../types';

export interface QuizIntroScreenProps {
  flowName: string;
  intro: QuizIntroResponse | null;
  isStarting: boolean;
  onStart: () => void;
  onStartVoiceMode?: () => void;
  onBackHome?: () => void;
}

interface MetadataRowProps {
  icon: ElementType;
  label: string;
  value: ReactNode;
}

const toRoundedNumber = (value: number | string | null | undefined): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return Math.round(value);
  }
  if (typeof value === 'string') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return Math.round(parsed);
    }
  }
  return null;
};

const MetadataRow = ({ icon: Icon, label, value }: MetadataRowProps) => (
  <div className="flex items-center justify-between py-2.5 text-sm">
    <span className="flex items-center gap-2 text-muted-foreground">
      <Icon className="size-4 shrink-0" aria-hidden="true" />
      {label}
    </span>
    <span className="font-medium text-foreground">{value}</span>
  </div>
);

export const QuizIntroScreen = ({
  flowName,
  intro,
  isStarting,
  onStart,
  onStartVoiceMode,
  onBackHome,
}: QuizIntroScreenProps) => {
  const flow = intro?.flow;
  const title = flow?.title?.trim() || flowName.trim() || flow?.name?.trim() || 'Quiz';
  const description = flow?.description || 'Review the details below, then begin when you are ready.';
  const isPassOnly =
    Boolean(intro?.is_pass_only ?? flow?.is_pass_only) ||
    flow?.passing_score_percentage === null;
  const passingScore = toRoundedNumber(flow?.passing_score_percentage);
  const attemptsUsed = intro?.attempts_used ?? 0;
  const maxAttempts = flow?.max_attempts ?? intro?.max_attempts ?? null;
  const totalSteps = intro?.total_questions ?? null;
  const scoreableQuestions = intro?.scoreable_questions ?? null;
  const estimatedDuration = flow?.estimated_duration_minutes ?? null;
  const hasInProgress = Boolean(intro?.in_progress_attempt);
  const inProgressAttemptNumber = intro?.in_progress_attempt?.attempt_number;
  const attemptNumberToDisplay =
    typeof inProgressAttemptNumber === 'number' && inProgressAttemptNumber > 0
      ? inProgressAttemptNumber
      : hasInProgress
      ? attemptsUsed
      : attemptsUsed + 1;

  return (
    <div data-flow-stage-active="true" className="flex items-start justify-center py-6 sm:items-center sm:py-16">
      <Card className="w-full max-w-xl" data-flow-surface="card" data-testid="quiz-intro-card">
        <CardTitle title={title} subtitle={description} subtitleSize='big'/>

        <CardContent className="flex flex-col gap-0">
          <div className="divide-y divide-border w-full">
            {!isPassOnly && passingScore !== null ? (
              <MetadataRow
                icon={Target}
                label="Passing score"
                value={<>{passingScore}%</>}
              />
            ) : null}

            {!isPassOnly ? (
              <MetadataRow
                icon={RotateCcw}
                label="Attempt"
                value={
                  maxAttempts !== null
                    ? `${attemptNumberToDisplay} of ${maxAttempts}`
                    : `${attemptNumberToDisplay}`
                }
              />
            ) : null}

            {totalSteps !== null ? (
              <MetadataRow icon={CheckCircle2} label="Total steps" value={totalSteps} />
            ) : null}

            {!isPassOnly && scoreableQuestions !== null ? (
              <MetadataRow icon={Target} label="Scored questions" value={scoreableQuestions} />
            ) : null}

            {estimatedDuration !== null ? (
              <MetadataRow icon={Clock} label="Estimated duration" value={`${estimatedDuration} min`} />
            ) : null}
          </div>

          {isPassOnly ? (
            <p className="mt-3 text-xs text-muted-foreground">
              This survey allows one attempt and is marked complete on submit.
            </p>
          ) : null}

          {hasInProgress ? (
            <Alert className="mt-4">
              <div className="flex items-start gap-2">
                <Info className="mt-0.5 size-4 shrink-0" />
                <div className="flex flex-col">
                  <AlertTitle className="leading-none">In-progress attempt</AlertTitle>
                  <AlertDescription className="mt-1 text-xs sm:text-sm">
                    You have an in-progress {isPassOnly ? 'survey' : 'quiz'} attempt. Resuming will continue where you left off.
                  </AlertDescription>
                </div>
              </div>
            </Alert>
          ) : null}
        </CardContent>

        <CardFooter className="flex flex-col gap-3 sm:flex-row sm:justify-between pt-5">
          {onBackHome ? (
            <Button
              variant="ghost"
              onClick={onBackHome}
              className="w-full sm:w-auto"
            >
              <ArrowLeft className="mr-2 size-4" />
              Back to Home
            </Button>
          ) : null}

          {onStartVoiceMode ? (
            <Button variant="secondary" onClick={onStartVoiceMode} disabled={isStarting}
              className="w-full sm:w-auto">
              {hasInProgress ? 'Resume in Voice Mode' : 'Start in Voice Mode'}
            </Button>
          ) : null}

          <Button
            onClick={onStart}
            disabled={isStarting}
            className="w-full sm:ml-auto sm:w-auto"
          >
            {isStarting ? (
              <>
                <Loader2 className="mr-2 size-4 animate-spin" />
                Starting...
              </>
            ) : hasInProgress ? (
              <>
                <Play className="mr-2 size-4" />
                {isPassOnly ? 'Resume Survey' : 'Resume Quiz'}
              </>
            ) : (
              <>
                <Play className="mr-2 size-4" />
                {isPassOnly ? 'Start Survey' : 'Start Quiz'}
              </>
            )}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
};

export default QuizIntroScreen;
